/**
 * copy-chart-button.tsx — "copy this chart as an image".
 *
 * Point it at a ref on whatever wraps the chart; it finds the SVG inside
 * (`findChartSvg`), rasterizes it (`svg-raster.ts`) and puts a PNG on the
 * clipboard (`clipboard-image.ts`).
 *
 * `useCopyChart` is the same thing without the button, for the metric cards —
 * where the action belongs in the menu that is already in the header rather
 * than as a fourth control beside a 48px sparkline. One implementation, two
 * presentations.
 *
 * Two decisions worth keeping:
 *
 * - **The export is opaque, unlike the board's.** A chart is drawn in
 *   `--muted-foreground` and `--chart-grid`, which are legible against the
 *   page and invisible on the white slide this is being pasted into. So the
 *   page background is painted behind it. A board's objects carry their own
 *   fills and survive a transparent background, which is more useful there.
 * - **The button reports what actually happened.** `copyCanvas` falls back to
 *   a download wherever the Clipboard API is missing — which includes any
 *   non-secure context, so anyone reaching this tenant by LAN IP rather than
 *   localhost gets the fallback every time. Saying "Copied" there would be a
 *   lie about where the file went.
 */
import * as React from 'react'
import { Check, Copy, Download } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { copyCanvas, imageFilename, type CopyOutcome } from '@/lib/clipboard-image'
import { findChartSvg, svgToCanvas } from '@/lib/svg-raster'

export function useCopyChart(targetRef: React.RefObject<HTMLElement | null>, name: string) {
  const [outcome, setOutcome] = React.useState<CopyOutcome | null>(null)
  const [busy, setBusy] = React.useState(false)
  const timer = React.useRef<number | null>(null)

  React.useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current)
    },
    [],
  )

  const copy = React.useCallback(async () => {
    const svg = findChartSvg(targetRef.current)
    const flash = (next: CopyOutcome) => {
      setOutcome(next)
      if (timer.current) window.clearTimeout(timer.current)
      timer.current = window.setTimeout(() => setOutcome(null), 2000)
    }
    if (!svg) return flash('failed')
    setBusy(true)
    try {
      // Read at click time rather than captured once: the theme can change
      // under the page without this component re-rendering.
      const bg = getComputedStyle(document.documentElement)
        .getPropertyValue('--background')
        .trim()
      const canvas = await svgToCanvas(svg, {
        background: bg ? `hsl(${bg})` : '#ffffff',
        padding: 12,
      })
      flash(await copyCanvas(canvas, imageFilename([name], 'chart')))
    } catch {
      flash('failed')
    } finally {
      setBusy(false)
    }
  }, [targetRef, name])

  return { copy, outcome, busy }
}

export function CopyChartButton({
  targetRef,
  name,
  label = 'Copy chart',
}: {
  targetRef: React.RefObject<HTMLElement | null>
  /** Used for the download filename, and to tell charts apart in a folder. */
  name: string
  label?: string
}) {
  const { copy, outcome, busy } = useCopyChart(targetRef, name)

  const text =
    outcome === 'copied'
      ? 'Copied'
      : outcome === 'downloaded'
        ? 'Saved'
        : outcome === 'failed'
          ? "Couldn't copy"
          : label

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => void copy()}
      disabled={busy}
      title="Copy this chart to the clipboard as a PNG"
      aria-label={`${label} as an image`}
    >
      {outcome === 'copied' ? <Check /> : outcome === 'downloaded' ? <Download /> : <Copy />}
      {text}
    </Button>
  )
}
